import {useCallback, useEffect, useRef, useState} from 'react';
import type {HierarchyTreeNode} from 'src/design/organisms/HierarchyTree';
import {findHierarchyExpansionPath} from 'src/design/organisms/hierarchyTreeUtils';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {
  setTreeBrowserExpandedIds,
  setTreeBrowserSelectedId,
} from 'src/redux/slices/treeBrowserSlice';

export interface UseTreeBrowserOptions {
  /** Namespaces the shared expansion and selection state, one per module browser. */
  expansionKey: string;
  defaultExpandedIds?: string[];
  /** Undefined while the page is still resolving its record; the stored id holds until then. */
  selectedId?: string;
  rootId: string;
}

export interface UseTreeBrowserResult {
  /** `selectedId` once resolved, otherwise the last stored selection, otherwise `rootId`. */
  effectiveSelectedId: string;
  expandedIds: string[];
  setExpandedIds: (ids: string[]) => void;
  search: string;
  setSearch: (search: string) => void;
  expandTo: (nodes: HierarchyTreeNode[], id: string) => void;
}

/** Tree expansion and selection (shared per key so they survive route changes) plus local search. */
export const useTreeBrowser = ({
  expansionKey,
  defaultExpandedIds = [],
  selectedId,
  rootId,
}: UseTreeBrowserOptions): UseTreeBrowserResult => {
  const dispatch = useAppDispatch();
  const stored = useAppSelector(
    state => state.treeBrowser.expandedIds[expansionKey]
  );
  const storedSelectedId = useAppSelector(
    state => state.treeBrowser.selectedIds[expansionKey]
  );
  const effectiveSelectedId = selectedId ?? storedSelectedId ?? rootId;

  useEffect(() => {
    if (selectedId === undefined || selectedId === storedSelectedId) return;
    dispatch(setTreeBrowserSelectedId({key: expansionKey, id: selectedId}));
  }, [dispatch, expansionKey, selectedId, storedSelectedId]);

  const fallbackRef = useRef(defaultExpandedIds);
  const expandedIds = stored ?? fallbackRef.current;
  const expandedRef = useRef(expandedIds);
  expandedRef.current = expandedIds;
  const [search, setSearch] = useState('');

  const setExpandedIds = useCallback(
    (ids: string[]) =>
      dispatch(setTreeBrowserExpandedIds({key: expansionKey, ids})),
    [dispatch, expansionKey]
  );

  const expandTo = useCallback(
    (nodes: HierarchyTreeNode[], id: string) => {
      // Expanding a leaf changes nothing on screen but costs a full tree re-render.
      const path = findHierarchyExpansionPath(nodes, id) ?? [];
      const current = expandedRef.current;
      const expanded = new Set(current);
      const additions = path.filter(nodeId => !expanded.has(nodeId));
      if (additions.length === 0) return;
      dispatch(
        setTreeBrowserExpandedIds({
          key: expansionKey,
          ids: [...current, ...additions],
        })
      );
    },
    [dispatch, expansionKey]
  );

  return {
    effectiveSelectedId,
    expandedIds,
    setExpandedIds,
    search,
    setSearch,
    expandTo,
  };
};
